import { BigNumber, utils } from 'ethers';
import { sendMetaTransaction } from 'decentraland-transactions';
import { Contract } from '@ethersproject/contracts';
import { getConnectedProvider } from '../../../lib/eth';
import { getTargetNetworkProvider } from './getTargetNetworkProvider';
import { transactionEvents } from './transactionEvents';
import { TransactionEventType } from './types';
import { getTransactionsApiUrl } from './urls';
import { getProviderChainId } from './getProviderChainId';
const acceptOrRejectTransaction = () => {
    return new Promise((resolve, reject) => {
        const acceptListener = () => {
            transactionEvents.removeAllListeners(TransactionEventType.REJECT);
            resolve(true);
        };
        const rejectListener = () => {
            transactionEvents.removeAllListeners(TransactionEventType.ACCEPT);
            reject(new Error('User rejected transaction'));
        };
        transactionEvents.addListener(TransactionEventType.ACCEPT, acceptListener);
        transactionEvents.addListener(TransactionEventType.REJECT, rejectListener);
    });
};
export async function sendTransaction(...args) {
    const [contract, contractMethodNameOrGetPopulatedTransaction, ...contractArguments] = args;
    try {
        const connectedProvider = await getConnectedProvider();
        if (!connectedProvider) {
            throw new Error('Provider not connected');
        }
        const chainId = await getProviderChainId(connectedProvider);
        const targetNetworkProvider = await getTargetNetworkProvider(contract.chainId);
        const contractInstance = new Contract(contract.address, contract.abi, targetNetworkProvider);
        // Populate the transaction data
        const unsignedTx = await (typeof contractMethodNameOrGetPopulatedTransaction ===
            'function'
            ? contractMethodNameOrGetPopulatedTransaction(contractInstance.populateTransaction)
            : contractInstance.populateTransaction[contractMethodNameOrGetPopulatedTransaction](...contractArguments));
        // If the connected provider is in the target network, use it to sign and send the tx
        if (chainId === contract.chainId) {
            const signer = targetNetworkProvider.getSigner();
            // Only on magic, prompt the user to accept or reject the transaction via the Web2TransactionModal
            if (connectedProvider.isMagic) {
                let transactionGasPrice = BigNumber.from(0);
                try {
                    // Compute the cost of the transaction
                    const gasPrice = await signer.getGasPrice();
                    const transactionGasCost = await signer.estimateGas(unsignedTx);
                    transactionGasPrice = gasPrice.mul(transactionGasCost);
                }
                catch (_e) { }
                // If the transaction gas price is not zero, we need to wait for the user to accept or reject the transaction
                if (!transactionGasPrice.isZero()) {
                    const acceptOrRejectPromise = acceptOrRejectTransaction();
                    const userBalance = await signer.getBalance();
                    transactionEvents.emit(TransactionEventType.PROMPT, {
                        transactionGasPrice: utils.formatEther(transactionGasPrice),
                        userBalance: utils.formatEther(userBalance),
                        chainId: contract.chainId
                    });
                    // Wait for the user to accept or reject the transaction in the Web2TransactionModal
                    await acceptOrRejectPromise;
                }
            }
            const tx = await signer.sendTransaction(unsignedTx);
            transactionEvents.emit(TransactionEventType.SUCCESS, { txHash: tx.hash });
            return tx.hash;
        }
        else {
            // otherwise, send it as a meta tx
            const txHash = await sendMetaTransaction(connectedProvider, targetNetworkProvider, unsignedTx.data, contract, {
                serverURL: getTransactionsApiUrl()
            });
            transactionEvents.emit(TransactionEventType.SUCCESS, { txHash });
            return txHash;
        }
    }
    catch (error) {
        const data = {
            type: TransactionEventType.ERROR,
            error: error
        };
        transactionEvents.emit(TransactionEventType.ERROR, data);
        throw error;
    }
}
//# sourceMappingURL=sendTransaction.js.map